import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, User, Loader2, ChevronRight, Phone, MapPin, ArrowRight, UserPlus } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_LINK = 'https://wa.me/918459458536';

const SAI_ICON = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 2L2 7l10 5 10-5-10-5z" fill="currentColor" opacity="0.2" />
    <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
  </svg>
);

const INITIAL_SUGGESTIONS = [
  "Show me luxury properties in Pune",
  "Compare top projects in Baner",
  "What's available under 3 Cr?",
  "I'm an NRI looking to invest",
];

const PropertyCard = ({ property }) => {
  const img = property.images?.[0] || '';
  return (
    <a
      href={`/properties/${property.id}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`chat-property-card-${property.id}`}
      className="block bg-white/[0.04] border border-white/10 rounded-lg overflow-hidden hover:border-[#E85D04]/40 transition-all group"
    >
      {img && (
        <div className="h-28 overflow-hidden">
          <img src={img} alt={property.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        </div>
      )}
      <div className="p-3">
        <p className="text-white text-sm font-semibold truncate">{property.name}</p>
        <p className="text-white/50 text-xs truncate">{property.developer}</p>
        <div className="flex items-center gap-2 mt-1.5 text-xs text-white/40">
          <MapPin size={10} />
          <span className="truncate">{property.area}</span>
          {property.bedrooms && <span className="ml-auto text-[#E85D04]/80">{property.bedrooms}</span>}
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-[#E85D04] font-medium">{property.price_range || 'Price on Request'}</span>
          <ArrowRight size={12} className="text-white/30 group-hover:text-[#E85D04] transition-colors" />
        </div>
      </div>
    </a>
  );
};

const LeadCaptureForm = ({ sessionId, onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/chat/capture-lead`, { session_id: sessionId, ...form });
      setDone(true);
      onSubmit?.(form);
      setTimeout(() => onClose(), 2000);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-center">
        <p className="text-green-400 text-sm font-semibold">Thank you! Our team will reach out shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-3 bg-white/[0.04] border border-[#E85D04]/20 rounded-lg space-y-2">
      <p className="text-xs text-white/60 mb-1">Share your details and our expert will connect with you</p>
      <input
        type="text" placeholder="Your Name *" value={form.name}
        onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
        className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white text-xs placeholder:text-white/30 focus:outline-none focus:border-[#E85D04]/50"
        data-testid="chat-lead-name"
      />
      <input
        type="tel" placeholder="Phone Number *" value={form.phone}
        onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
        className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white text-xs placeholder:text-white/30 focus:outline-none focus:border-[#E85D04]/50"
        data-testid="chat-lead-phone"
      />
      <input
        type="email" placeholder="Email (optional)" value={form.email}
        onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
        className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white text-xs placeholder:text-white/30 focus:outline-none focus:border-[#E85D04]/50"
        data-testid="chat-lead-email"
      />
      <button
        type="submit" disabled={!form.name.trim() || !form.phone.trim() || submitting}
        data-testid="chat-lead-submit"
        className="w-full py-2 rounded bg-gradient-to-r from-[#E85D04] to-[#DC2626] text-white text-xs font-semibold disabled:opacity-40 hover:brightness-110 transition"
      >
        {submitting ? 'Submitting...' : 'Connect with Expert'}
      </button>
    </form>
  );
};

export const ChatBot = ({ projectContext = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('sai_session_id');
    if (stored) return stored;
    const id = `sai-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('sai_session_id', id);
    return id;
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [hasGreeted, setHasGreeted] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);

  // Load greeting on first open
  useEffect(() => {
    if (isOpen && !hasGreeted && messages.length === 0) {
      setHasGreeted(true);
      setMessages([{
        role: 'assistant',
        content: "Hey there! I'm SAI — your real estate advisor at Prop Assist.\n\nWhether you're scouting luxury residences, commercial spaces, or just exploring options in Pune — I've got you covered.\n\nWhat's on your mind?",
        suggested_replies: INITIAL_SUGGESTIONS,
        properties: [],
      }]);
    }
  }, [isOpen, hasGreeted, messages.length]);

  const handleSend = useCallback(async (text) => {
    const userMessage = (text || input).trim();
    if (!userMessage || isLoading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage,
        project_context: projectContext,
      });

      const data = res.data;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        properties: data.properties || [],
        suggested_replies: data.suggested_replies || [],
        intent: data.intent,
        capture_lead: data.capture_lead,
      }]);

      // Show lead form if AI flagged capture
      if (data.capture_lead && !leadCaptured) {
        setShowLeadForm(true);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Apologies — I'm having a connectivity issue. Please reach out to Vijay directly on WhatsApp for immediate help.",
        suggested_replies: ["Try again", "Chat on WhatsApp"],
        properties: [],
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, sessionId, projectContext, leadCaptured]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleSuggestion = (text) => {
    if (text === "Chat on WhatsApp") {
      window.open(WHATSAPP_LINK, '_blank');
      return;
    }
    handleSend(text);
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-6 z-[9999] w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${
          isOpen
            ? 'bg-white/10 backdrop-blur-xl border border-white/20 rotate-90'
            : 'bg-gradient-to-r from-[#E85D04] to-[#DC2626] hover:scale-110 hover:shadow-[0_0_20px_rgba(232,93,4,0.4)]'
        }`}
      >
        {isOpen ? <X size={22} className="text-white" /> : <MessageCircle size={22} className="text-white" />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          data-testid="chat-window"
          className="fixed bottom-44 right-6 z-[9999] w-[400px] max-w-[calc(100vw-48px)] h-[540px] max-h-[calc(100vh-200px)]
                     bg-[#0a0a0a]/95 backdrop-blur-xl border border-white/10 rounded-xl
                     flex flex-col overflow-hidden shadow-2xl"
          style={{ animation: 'slideInUp 0.3s ease-out' }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#E85D04] to-[#DC2626]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white">
                <SAI_ICON />
              </div>
              <div className="flex-1">
                <h3 className="text-white text-sm font-semibold tracking-wide">SAI</h3>
                <p className="text-[10px] text-white/70">Senior AI Advisory Intelligence  |  Prop Assist</p>
              </div>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                 className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition"
                 title="Chat on WhatsApp">
                <Phone size={14} className="text-white" />
              </a>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 scrollbar-hide">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {/* Avatar */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  msg.role === 'user' ? 'bg-[#E85D04]/20' : 'bg-white/[0.06]'
                }`}>
                  {msg.role === 'user'
                    ? <User size={13} className="text-[#E85D04]" />
                    : <span className="text-[#E85D04] text-xs"><SAI_ICON /></span>}
                </div>

                {/* Content */}
                <div className={`max-w-[82%] space-y-2 ${msg.role === 'user' ? 'items-end' : ''}`}>
                  <div className={`px-3 py-2.5 rounded-xl text-[13px] leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#E85D04]/15 text-white rounded-br-sm'
                      : 'bg-white/[0.04] text-white/90 rounded-bl-sm'
                  }`}>
                    {msg.content.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <br />}
                        <span dangerouslySetInnerHTML={{
                          __html: line.replace(
                            /https:\/\/wa\.me\/\d+/g,
                            m => `<a href="${m}" target="_blank" rel="noopener noreferrer" class="text-[#E85D04] underline decoration-[#E85D04]/30 hover:decoration-[#E85D04]">WhatsApp</a>`
                          )
                        }} />
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Property Cards */}
                  {msg.properties?.length > 0 && (
                    <div className={`grid gap-2 ${msg.properties.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      {msg.properties.map(p => <PropertyCard key={p.id} property={p} />)}
                    </div>
                  )}

                  {/* Lead capture trigger */}
                  {msg.capture_lead && !leadCaptured && idx === messages.length - 1 && (
                    <button onClick={() => setShowLeadForm(true)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#E85D04]/10 border border-[#E85D04]/20 text-[#E85D04] text-xs hover:bg-[#E85D04]/20 transition"
                      data-testid="chat-connect-expert-btn">
                      <UserPlus size={14} /> Connect with our expert
                    </button>
                  )}

                  {/* Suggested Replies */}
                  {msg.suggested_replies?.length > 0 && idx === messages.length - 1 && !isLoading && (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {msg.suggested_replies.map((sr, si) => (
                        <button key={si} onClick={() => handleSuggestion(sr)}
                          data-testid={`chat-suggestion-${si}`}
                          className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/60 text-[11px]
                                     hover:bg-[#E85D04]/10 hover:border-[#E85D04]/30 hover:text-white transition-all">
                          {sr}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Loading */}
            {isLoading && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 rounded-full bg-white/[0.06] flex items-center justify-center">
                  <span className="text-[#E85D04] text-xs"><SAI_ICON /></span>
                </div>
                <div className="bg-white/[0.04] px-4 py-3 rounded-xl rounded-bl-sm">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-[#E85D04]/60 rounded-full animate-bounce" style={{animationDelay:'0ms'}} />
                    <span className="w-1.5 h-1.5 bg-[#E85D04]/60 rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
                    <span className="w-1.5 h-1.5 bg-[#E85D04]/60 rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
                  </div>
                </div>
              </div>
            )}

            {/* Lead Capture Form */}
            {showLeadForm && !leadCaptured && (
              <LeadCaptureForm
                sessionId={sessionId}
                onClose={() => setShowLeadForm(false)}
                onSubmit={() => setLeadCaptured(true)}
              />
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-white/10 bg-black/40">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask SAI anything about properties..."
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-3.5 py-2.5 text-white
                           placeholder:text-white/25 focus:outline-none focus:border-[#E85D04]/40 text-[13px]"
              />
              <button
                data-testid="chat-send-btn"
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading}
                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                  input.trim() && !isLoading
                    ? 'bg-gradient-to-r from-[#E85D04] to-[#DC2626] text-white hover:brightness-110'
                    : 'bg-white/[0.04] text-white/20 cursor-not-allowed'
                }`}
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </>
  );
};
